import {
    Typography, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    CircularProgress, Snackbar, Alert, IconButton, InputAdornment, Tooltip
} from '@mui/material'
import { Add, CloudUpload, Search as SearchIcon, Edit, DeleteOutline } from '@mui/icons-material'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import _ from 'lodash'
import EmptyView from '../common/EmptyView'
import Show from '../common/show'
import { Api } from '../../helpers'
import { uploadToSpaces, fileToBase64 } from '../../helpers/uploadToSpaces'

export interface Sponsor {
    _id: string
    name: string
    banner: string
}

interface FormState {
    name: string
    bannerFile: File | null
    bannerPreview: string | null
    existingBanner: string | null
}

interface StateProps {
    sponsors: Sponsor[]
    loading: boolean
    searchVal: string
    showModal: boolean
    editingSponsorId: string | null
    deletingSponsorId: string | null
    saving: boolean
    form: FormState
    error: string | null
}

const defaultForm: FormState = {
    name: '',
    bannerFile: null,
    bannerPreview: null,
    existingBanner: null
}

const headers = ['Banner', 'Name', 'Actions']

const Sponsors: FC = () => {
    const [state, setState] = useState<StateProps>({
        sponsors: [],
        loading: true,
        searchVal: '',
        showModal: false,
        editingSponsorId: null,
        deletingSponsorId: null,
        saving: false,
        form: defaultForm,
        error: null
    })
    const { sponsors, loading, searchVal, showModal, editingSponsorId, deletingSponsorId, saving, form, error } = state
    const fileInputRef = useRef<HTMLInputElement>(null)

    const setForm = (patch: Partial<FormState>) =>
        setState(s => ({ ...s, form: { ...s.form, ...patch } }))

    // No dedicated search endpoint - the sponsor roster is a short admin-managed list, not
    // something worth a server round trip per keystroke, so filtering happens client-side (same
    // reasoning as the Rankings screen's own country/name filters in the mobile app).
    const getSponsors = () => {
        setState(s => ({ ...s, loading: true }))
        Api({ controller: 'Sponsors', action: 'getSponsors' })
            .then((resp: any) => setState(s => ({ ...s, sponsors: resp || [], loading: false })))
            .catch(() => setState(s => ({ ...s, loading: false })))
    }

    const filtered = useMemo(() => {
        if (!searchVal.trim()) return sponsors
        const val = searchVal.toLowerCase()
        return sponsors.filter(s => s.name.toLowerCase().includes(val))
    }, [sponsors, searchVal])

    const debouncedSearch = useCallback(_.debounce((val: string) => setState(s => ({ ...s, searchVal: val })), 300), [])

    const openAdd = () =>
        setState(s => ({ ...s, showModal: true, editingSponsorId: null, form: defaultForm }))

    const openEdit = (sponsor: Sponsor) =>
        setState(s => ({
            ...s,
            showModal: true,
            editingSponsorId: sponsor._id,
            form: { name: sponsor.name, bannerFile: null, bannerPreview: null, existingBanner: sponsor.banner }
        }))

    const deleteSponsor = (sponsorId: string) => {
        setState(s => ({ ...s, deletingSponsorId: sponsorId }))
        Api({ controller: 'Sponsors', action: 'deleteSponsor', sponsorId })
            .then(() => {
                setState(s => ({ ...s, deletingSponsorId: null }))
                getSponsors()
            })
            .catch((err: any) =>
                setState(s => ({ ...s, deletingSponsorId: null, error: err?.message || 'Failed to remove sponsor.' }))
            )
    }

    const handleBannerSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return
        const reader = new FileReader()
        reader.onload = ev => setForm({ bannerFile: file, bannerPreview: ev.target?.result as string })
        reader.readAsDataURL(file)
        e.target.value = ''
    }

    const saveSponsor = async () => {
        setState(s => ({ ...s, saving: true }))
        try {
            let banner = form.existingBanner
            if (form.bannerFile) {
                const [base64File] = await Promise.all([fileToBase64(form.bannerFile)])
                const [newUrl] = await uploadToSpaces([base64File], 'sponsors')
                banner = newUrl
            }

            const resp: any = await Api({
                controller: 'Sponsors',
                action: editingSponsorId ? 'updateSponsor' : 'addSponsor',
                ...(editingSponsorId && { sponsorId: editingSponsorId }),
                name: form.name,
                banner
            })

            if (resp?.error)
                setState(s => ({ ...s, saving: false, error: resp.error.message }))
            else {
                setState(s => ({ ...s, saving: false, showModal: false, editingSponsorId: null, form: defaultForm }))
                getSponsors()
            }
        } catch (err: any) {
            setState(s => ({ ...s, saving: false, error: err?.message || 'Something went wrong.' }))
        }
    }

    const canSave = !saving && !!form.name.trim() && Boolean(form.bannerFile || form.existingBanner)

    useEffect(() => { getSponsors() }, [])

    return (
        <Box>
            <Box sx={{ padding: 2, textAlign: 'left' }}>
                <Typography sx={{ color: '#000', fontSize: 25, fontWeight: 'bold' }}>Event Sponsors</Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: '5px', mb: 1 }}>
                <TextField
                    size='small'
                    placeholder='Search sponsors...'
                    onChange={e => debouncedSearch(e.target.value)}
                    sx={{ flex: 1, bgcolor: '#fff', borderRadius: 2 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position='start'>
                                <SearchIcon sx={{ fontSize: 20, color: '#9ca3af' }} />
                            </InputAdornment>
                        )
                    }}
                />
                <Button
                    variant='contained'
                    startIcon={<Add />}
                    onClick={openAdd}
                    sx={{ borderRadius: 2, whiteSpace: 'nowrap' }}
                >
                    Add Sponsor
                </Button>
            </Box>

            <Show>
                <Show.When isTrue={loading}>
                    <EmptyView loading={loading} oopTxt='No Sponsors' text='No sponsors found.' />
                </Show.When>
                <Show.Else>
                    <TableContainer component={Paper} sx={{ mt: 1, mx: '5px' }}>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: 'primary.main' }}>
                                    {headers.map(h => (
                                        <TableCell key={h} sx={{ color: 'white', fontWeight: 'bold' }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filtered.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={3} align='center' sx={{ color: '#9ca3af', py: 4 }}>
                                            {searchVal ? 'No sponsors match your search.' : 'No sponsors yet. Add your first one.'}
                                        </TableCell>
                                    </TableRow>
                                ) : filtered.map(sponsor => (
                                    <TableRow key={sponsor._id} hover>
                                        <TableCell>
                                            {/* Avatar's default object-fit:cover cropped every logo to fill a
                                                fixed box, which looked fine only for the ones that happened to
                                                match its aspect ratio - a wide wordmark or a tall badge got
                                                zoomed in and cut off. A plain img with object-fit:contain on a
                                                neutral card shows the whole logo, uncropped, at a consistent
                                                size regardless of its own proportions. */}
                                            <Box
                                                sx={{
                                                    width: 84,
                                                    height: 44,
                                                    borderRadius: 1.5,
                                                    bgcolor: '#fff',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    p: 0.5
                                                }}
                                            >
                                                <Box
                                                    component='img'
                                                    src={sponsor.banner}
                                                    alt={sponsor.name}
                                                    sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                                />
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>{sponsor.name}</TableCell>
                                        <TableCell>
                                            <Tooltip title='Edit'>
                                                <IconButton size='small' onClick={() => openEdit(sponsor)} sx={{ color: 'primary.main' }}>
                                                    <Edit fontSize='small' />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title='Delete'>
                                                <IconButton
                                                    size='small'
                                                    onClick={() => deleteSponsor(sponsor._id)}
                                                    disabled={deletingSponsorId === sponsor._id}
                                                    sx={{ color: '#ef4444', ml: 0.5 }}
                                                >
                                                    {deletingSponsorId === sponsor._id
                                                        ? <CircularProgress size={14} color='inherit' />
                                                        : <DeleteOutline fontSize='small' />}
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Show.Else>
            </Show>

            {/* Add / Edit Dialog */}
            <Dialog open={showModal} onClose={() => !saving && setState(s => ({ ...s, showModal: false }))} fullWidth maxWidth='sm'>
                <DialogTitle sx={{ fontWeight: 'bold' }}>
                    {editingSponsorId ? 'Edit Sponsor' : 'Add Sponsor'}
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
                    <TextField
                        label='Company Name'
                        value={form.name}
                        onChange={e => setForm({ name: e.target.value })}
                        fullWidth
                        size='small'
                    />

                    <Box>
                        <input
                            ref={fileInputRef}
                            type='file'
                            accept='image/*'
                            style={{ display: 'none' }}
                            onChange={handleBannerSelect}
                        />
                        <Button
                            variant='outlined'
                            startIcon={<CloudUpload />}
                            onClick={() => fileInputRef.current?.click()}
                            sx={{ borderRadius: 2, borderStyle: 'dashed', textTransform: 'none' }}
                            fullWidth
                        >
                            {form.bannerPreview || form.existingBanner ? 'Replace Banner' : 'Upload Banner'}
                        </Button>

                        {(form.bannerPreview || form.existingBanner) && (
                            // object-fit:contain (not cover, which cropped it) so this preview
                            // shows the logo exactly as the table will - full, uncropped.
                            <Box
                                sx={{
                                    mt: 1.5,
                                    width: '100%',
                                    height: 140,
                                    borderRadius: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    p: 1.5
                                }}
                            >
                                <Box
                                    component='img'
                                    src={form.bannerPreview || form.existingBanner || undefined}
                                    alt='Sponsor banner'
                                    sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                />
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setState(s => ({ ...s, showModal: false }))} disabled={saving}>Cancel</Button>
                    <Button
                        variant='contained'
                        onClick={saveSponsor}
                        disabled={!canSave}
                        startIcon={saving ? <CircularProgress size={16} color='inherit' /> : undefined}
                    >
                        {editingSponsorId ? 'Update' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={Boolean(error)}
                autoHideDuration={4000}
                onClose={() => setState(s => ({ ...s, error: null }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity='error' onClose={() => setState(s => ({ ...s, error: null }))} sx={{ width: '100%' }}>
                    {error}
                </Alert>
            </Snackbar>
        </Box>
    )
}

export default Sponsors
